import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import AurLocationTreeView from '../eca-doc-list/eca-save-search/AurLocationTreeView';
import {
  ID_SUFFIX,
  LOAD_DOC_TYPE,
  MIN_SEARCH_CHAR,
} from '../../constants/constants';
import AurSearchBox from '../common/aur-search-box/AurSearchBox';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { getTagData } from '../../api/EcaManagementService';
import ECAFlyout from './ECAFlyout';
import AurButton from '../common/aur-button/AurButton';
import { TagData } from '../client-details/project-data-table/types';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { ExtendedTreeItemProps } from '../eca-doc-list/eca-save-search/EcaSaveSearch';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import { IGlobalSelectedItem } from '../../redux/slices/ecaAdhocSearchSlice';
import { orderBy } from 'lodash';
import {
  formatNumberWithComma,
  getExpandableIds,
  isEmpty,
} from '../../utils/utils';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../constants/analyticsConstants';
import { useAnalytics } from '../../hooks/useAnalytics';

interface IECATagTabProps {
  isShowSearch?: boolean;
  searchProps?: {
    value?: string;
  };
  isShowFlyout?: boolean;
  defaultSelectedTagIds?: string[];
  selectedItems?: string[];
  multipleSelection?: boolean;
  multipleSelectedLabel?: string;
  isShowCountLabel?: boolean;
  onTagsSelected?: (selectedTags?: ExtendedTreeItemProps[]) => void;
  onTagSelected?: (tagInfo?: ExtendedTreeItemProps) => void;
  onTagFolderSelected?:
  | ((tagInfo?: ExtendedTreeItemProps) => void | undefined)
  | undefined;
  handleViewDocs?: (flag: string, selectedItem: IGlobalSelectedItem) => void;
  activeTag?: TreeViewBaseItem<ExtendedTreeItemProps> | null | undefined;
  handleUseAsScope?: (flag: string, selectedItem: IGlobalSelectedItem) => void;
  drawerRef?: React.RefObject<HTMLDivElement>;
  cacheKey?: string;
  cachedTagData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  onTagDataChange?: (
    data?: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ) => void;
}

const ECATagTab: React.FC<IECATagTabProps> = ({
  isShowSearch = true,
  searchProps,
  isShowFlyout = true,
  selectedItems,
  multipleSelection,
  defaultSelectedTagIds,
  multipleSelectedLabel,
  isShowCountLabel,
  onTagsSelected,
  onTagSelected,
  onTagFolderSelected,
  handleViewDocs,
  activeTag = null,
  handleUseAsScope,
  drawerRef,
  cacheKey,
  cachedTagData,
  onTagDataChange,
}) => {
  const [tagData, setTagData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps> | null | undefined
  >(activeTag);

  const [loading, setLoading] = useState<boolean>(true);

  const [showFlyout, setShowFlyout] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [positions, setPositions] = useState({
    top: 0,
    left: 0,
  });
  const headerHeight = 72;
  const { projectId } = useSelector((state: RootState) => state.reports);
  const { clientId } = useSelector(
    (state: RootState) => state.selectedClient
  );
  const { showSetScopeLoader } = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );
  const { taggingStatus } = useSelector(
    (state: RootState) => state.ecaDocList
  );
  const { ecaDocumentViewerPopup: ecaDocumentViewer } = useSelector(
    (state: RootState) => state.ecaDocumentViewer
  );
  const [selectedTagTemp, setSelectedTagTemp] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps> | null | undefined
  >(selectedTag);
  const isFirstRender = useRef(true);
  const cacheKeyRef = useRef(cacheKey);
  const cachedTagDataRef = useRef(cachedTagData);
  const onTagDataChangeRef = useRef(onTagDataChange);
  const componentMountedRef = useRef(true);
  const taggingStatusRef = useRef(taggingStatus);

  cacheKeyRef.current = cacheKey;
  cachedTagDataRef.current = cachedTagData;
  onTagDataChangeRef.current = onTagDataChange;
  taggingStatusRef.current = taggingStatus;

  const isCacheEnabled = Boolean(cacheKey && onTagDataChange);
  const { trackGAEvent } = useAnalytics();

  useEffect(() => {
    if (searchProps) {
      setSearchKeyword(searchProps?.value ?? '');
    }
  }, [searchProps]);

  useEffect(() => {
    if (searchKeyword !== '') {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SEARCH,
        click: {
          name: t('gaTracking.ecaHomePage.filterList'),
          region: GA_REGIONS.LEFT_NAV_TAB,
          type: GA_CLICK_TYPES.LIST,
          action: t('gaTracking.ecaHomePage.searchedToFilter'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.tags'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
  }, [searchKeyword]);

  const defaultSelectedItem = useCallback(
    (tags: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      if (defaultSelectedTagIds) {
        let getField: TreeViewBaseItem<ExtendedTreeItemProps> | undefined;
        if (defaultSelectedTagIds.length > 0) {
          const fieldId = defaultSelectedTagIds?.[0];

          if (fieldId) {
            getField = tags.find(
              (item) =>
                item.children?.find((tag) => tag.id === fieldId.toString()) ||
                item.id === fieldId.toString()
            );
            if (getField?.id === fieldId.toString()) {
              onTagFolderSelected?.(getField);
            } else {
              if (getField) {
                onTagFolderSelected?.(getField);

                if (onTagsSelected) {
                  const defaultTags = [];
                  for (
                    let index = 0;
                    index < defaultSelectedTagIds.length;
                    index++
                  ) {
                    const defaultSelectedTagId = defaultSelectedTagIds?.[index];
                    const getTag = getField.children?.find(
                      (fieldItem) =>
                        fieldItem.id === defaultSelectedTagId.toString()
                    );
                    if (index === 0) {
                      setSelectedTag(getTag ?? getField);
                    }
                    if (getTag) {
                      defaultTags.push({
                        ...getTag,
                        createdBy: getField?.createdBy,
                        createdDate: getField?.createdDate,
                        updatedDate: getField?.updatedDate,
                        updatedBy: getField?.updatedBy,
                      });
                    }
                  }

                  onTagsSelected(defaultTags);
                }
              }
            }
          }
        }
      }
    },
    [defaultSelectedTagIds, onTagFolderSelected, onTagsSelected]
  );

  const mapTagData = useCallback((tagRes: TagData[]) => {
    return tagRes.map((item: TagData, pIndex: number) => {
      return {
        id: String(item.id) + ID_SUFFIX.PARENT_TAG + pIndex,
        label: item.label,
        name: item.name,
        fileType: item.label ? 'folder' : undefined,
        children:
          item.values.length > 0
            ? orderBy(item.values, ['displayOrder'], ['desc']).map(
                (itemChild, index) => {
                  return {
                    id:
                      String(itemChild.id) +
                      ID_SUFFIX.CHILD_TAG +
                      String(item.id) +
                      index,
                    label: itemChild.value,
                    count: itemChild.documentCount.toString(),
                    fieldId: String(item.id) + ID_SUFFIX.PARENT_TAG + pIndex,
                    parentFolder: item.label,
                    parentTagName: item.name,
                    displayOrder: itemChild.displayOrder,
                    createdBy: itemChild.createdBy ?? '',
                    createdDate: itemChild.createdOn ?? '',
                    updatedDate: itemChild.updatedOn ?? '',
                    updatedBy: itemChild.updatedBy ?? '',
                  };
                }
              )
            : undefined,
        createdBy: item.createdBy,
        createdDate: item.createdOn,
        updatedDate: item.updatedOn,
        updatedBy: item.updatedBy,
      };
    }) as TreeViewBaseItem<ExtendedTreeItemProps>[];
  }, []);

  const hydrateTagData = useCallback(
    (mappedArray: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      defaultSelectedItem(mappedArray);
      setTagData(mappedArray);
      setLoading(false);
    },
    [defaultSelectedItem]
  );

  const fetchTagData = useCallback(
    async (forceRefresh = false) => {
      const requestKey = cacheKey;
      const requestIsCurrent = () =>
        !isCacheEnabled || requestKey === cacheKeyRef.current;

      if (
        isCacheEnabled &&
        !forceRefresh &&
        cachedTagDataRef.current !== undefined
      ) {
        if (requestIsCurrent()) {
          hydrateTagData(cachedTagDataRef.current);
        }
        return;
      }

      if (!clientId || !projectId) {
        if (requestIsCurrent()) {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const paramString = `clientId=${clientId}&projectId=${projectId}`;
        const response = await getTagData(paramString);

        if (!requestIsCurrent() || !componentMountedRef.current) {
          return;
        }

        const mappedArray = mapTagData(response.data?.data ?? []);
        hydrateTagData(mappedArray);

        if (isCacheEnabled) {
          onTagDataChangeRef.current?.(mappedArray);
        }
      } catch (error) {
        if (requestIsCurrent() && componentMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [
      cacheKey,
      clientId,
      hydrateTagData,
      isCacheEnabled,
      mapTagData,
      projectId,
    ]
  );

  const previousCacheKey = useRef(cacheKey);
  const previousCachedTagData = useRef(cachedTagData);
  const previousRequestScope = useRef(`${clientId ?? ''}|${projectId ?? ''}`);
  const previousTaggingStatus = useRef(taggingStatus);
  const tagDataInitialized = useRef(false);

  useEffect(() => {
    const keyChanged = previousCacheKey.current !== cacheKey;
    const cacheDataChanged =
      previousCachedTagData.current !== cachedTagData;
    const requestScope = `${clientId ?? ''}|${projectId ?? ''}`;
    const scopeChanged = previousRequestScope.current !== requestScope;

    previousCacheKey.current = cacheKey;
    previousCachedTagData.current = cachedTagData;
    previousRequestScope.current = requestScope;

    const shouldForceInitialStatusRefresh =
      !tagDataInitialized.current &&
      taggingStatusRef.current === 'Completed';

    if (
      tagDataInitialized.current &&
      !keyChanged &&
      !cacheDataChanged &&
      !scopeChanged
    ) {
      return;
    }

    tagDataInitialized.current = true;

    if (
      isCacheEnabled &&
      cachedTagData !== undefined &&
      !shouldForceInitialStatusRefresh
    ) {
      hydrateTagData(cachedTagData);
      return;
    }

    if (projectId) {
      fetchTagData(
        shouldForceInitialStatusRefresh ||
          (isCacheEnabled && cacheDataChanged)
      );
    }
  }, [
    cacheKey,
    cachedTagData,
    clientId,
    hydrateTagData,
    isCacheEnabled,
    projectId,
    fetchTagData,
  ]);

  useEffect(() => {
    const isNewSuccessfulMutation =
      taggingStatus === 'Completed' &&
      previousTaggingStatus.current !== 'Completed';
    previousTaggingStatus.current = taggingStatus;

    if (isNewSuccessfulMutation) {
      setSelectedTag(selectedTagTemp);
      fetchTagData(true);
    }
  }, [fetchTagData, taggingStatus]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // skip effect on mount
    }

    if (!ecaDocumentViewer) {
      setSelectedTag(selectedTagTemp);
    }
  }, [ecaDocumentViewer, selectedTagTemp]);

  useEffect(() => {
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  const filteredTagData = useMemo(
    () =>
      tagData
        .map((item) => ({
          ...item,
          children:
            item.children?.filter(
              (child) =>
                child.label.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                item.label.toLowerCase().includes(searchKeyword.toLowerCase())
            ) || [],
        }))
        .filter(
          (item) =>
            item.children.length ||
            item.label.toLowerCase().includes(searchKeyword.toLowerCase())
        ),
    [tagData, searchKeyword]
  );

  const filteredTagExpandedIds: string[] = useMemo(
    () => (!isEmpty(searchKeyword) ? getExpandableIds(filteredTagData) : ['']),
    [filteredTagData, searchKeyword]
  );

  const treeDefaultExpandedIds = useMemo(() => {
    if (selectedTag && isEmpty(searchKeyword)) {
      return [
        selectedTag?.fieldId ? selectedTag?.fieldId : selectedTag?.id || '',
      ];
    }
    return filteredTagExpandedIds;
  }, [selectedTag, searchKeyword, filteredTagExpandedIds]);


  
  const handleClose = () => {
    setShowFlyout(false);
    setSelectedTag(null);
  };

  /**
   * Handle selected tree node
   * @param selectedNode
   * @param event
   */
  const handleSelectedItem = useCallback(
    (
      selectedNode: TreeViewBaseItem<ExtendedTreeItemProps>,
      event?: React.SyntheticEvent
    ) => {
      if (selectedNode.fileType !== 'folder') {
        setSelectedTag(selectedNode);
        if (event && isShowFlyout) {
          setAnchorEl(drawerRef?.current ?? (event.currentTarget as HTMLElement));
          const { top, right } = event.currentTarget.getBoundingClientRect();
          setPositions({
            top,
            left: right,
          });
          setShowFlyout(true);
          setTimeout(() => {
            document.getElementById('eca-flyout-container')?.focus();
          }, 100);
        }
        if (onTagSelected) {
          onTagSelected({
            ...selectedNode,
          });
        }
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: t('gaTracking.ecaHomePage.tagClick'),
            region: GA_REGIONS.WIDGET_LEFT_NAV,
            type: GA_CLICK_TYPES.LIST_ITEM,
            action: t('gaTracking.ecaHomePage.openedTaggingGroup'),
          },
          subPage: {
            subpage_name: t('gaTracking.ecaHomePage.tags'),
            subpage_type: GA_SUBPAGE_TYPE.TAB,
          },
        });
      } else {
        if (onTagFolderSelected) {
          onTagFolderSelected({
            ...selectedNode,
          });
        }
      }
    },
    [
      drawerRef,
      isShowFlyout,
      onTagFolderSelected,
      onTagSelected,
      trackGAEvent,
    ]
  );

  const renderTagFlyoutContent = () => {
    return (
      <div tabIndex={0} id="eca-flyout-container">
        <Box className="flyout-tag-container" component="aside" aria-labelledby='saved-search-flyout-tag'>
          <Box className="flyout-header">
            <AurButton
              classNames={'close-button'}
              label={t('buttons.close')}
              type="exit"
              size="Small"
              title={t('buttons.close')}
              dataTestId="close-btn"
              onClick={handleClose}
            />
            <Typography className="typo-text headline2_size custom-title-text" id="saved-search-flyout-tag">
              {selectedTag?.label}
            </Typography>
            <Typography className="typo-text subhead4_size custom-color-white">
              <span>{t('eca.flyout.lastDocCount')}</span>
              <span className="typo-text body2_size custom-color">
                {formatNumberWithComma(selectedTag?.count ?? 0)}
              </span>
            </Typography>
          </Box>
          <Box>
            <Box className="flyout-doc-button-container">
              <Box className="flyout-doc-text">
                <Typography className="typo-text body3_size custom-color">
                  {t('eca.flyout.viewDocTagTitle')}
                </Typography>
                <Typography className="typo-text label4_size custom-color-orange">
                  {t('eca.flyout.viewDocSubTitle')}
                </Typography>
              </Box>
              <AurButton
                classNames="doc-button"
                onClick={() => {
                  if (selectedTag) {
                    setSelectedTagTemp(selectedTag);
                    handleViewDocs?.(LOAD_DOC_TYPE.TAG_DOC, selectedTag);
                    handleClose();
                  }
                }}
                type="continue"
                size="Small"
                dataTestId="view-doc-button"
                label={t('eca.flyout.viewDocBtn')}
                minWidth={126}
              />
            </Box>
            <Divider orientation="horizontal" flexItem />
            <Box className="flyout-doc-button-container">
              <Box className="flyout-doc-text">
                <Typography className="typo-text body3_size custom-color">
                  {t('eca.flyout.scopeTagTitle')}
                </Typography>
              </Box>
              <AurButton
                classNames="doc-button"
                onClick={() => {
                  if (selectedTag) {
                    setSelectedTagTemp(selectedTag);
                    handleUseAsScope?.(LOAD_DOC_TYPE.TAGS_SCOPE, selectedTag);
                    handleClose();
                  }
                }}
                type="continue"
                size="Small"
                dataTestId="scope-button"
                label={t('eca.flyout.scopeBtn')}
                minWidth={126}
              />
            </Box>
          </Box>
        </Box>
      </div>
    );
  };

  return (
    <>
      {isShowSearch && (
        <Box
          className="tag-container search-tag-container"
          data-testid="search-container"
        >
          <AurSearchBox
            label={t('common.filterList')}
            minCharForSearch={MIN_SEARCH_CHAR}
            onSearch={setSearchKeyword}
            classNameProps="filter-tag-text-field"
          />
        </Box>
      )}
      <Box
        className="drawer-treeview-content custom-treeview tag-tree-scroll-view"
        data-testid="location-treeview-content"
      >
        {loading ? (
          <Box className="eca-tab-loader">
            <AurLoadingIndicator show={true} />
          </Box>
        ) : (
          <div className="wrap-tree-tag-flex">
            <AurLocationTreeView
              itemsList={filteredTagData}
              handleSelectedItem={handleSelectedItem}
              selectedItem={selectedTag?.id || ''}
              handleNoResultMessage={t('eca.flyout.noTags')}
              allowGlobalSelect={true}
              treeSelectedItems={selectedItems}
              multipleSelection={multipleSelection}
              multipleSelectedLabel={multipleSelectedLabel}
              isShowCountLabel={isShowCountLabel}
              defaultExpandedIds={treeDefaultExpandedIds}
            />
          </div>
        )}
      </Box>
      {isShowFlyout && (
        <ECAFlyout
          showFlyout={showFlyout}
          className="custom-tag-popper"
          anchorEl={anchorEl}
          onClose={handleClose}
          content={renderTagFlyoutContent()}
          containerStyle={{
            marginTop: headerHeight,
            marginBottom: headerHeight,
          }}
          positions={positions}
          type={'tag'}
        />
      )}
      {showSetScopeLoader && (
        <Box className="left-scope-loader">
          <AurLoadingIndicator
            show={showSetScopeLoader}
            className="loader-icon"
          />
        </Box>
      )}
    </>
  );
};

export default ECATagTab;
